import Modal from 'components/common/modal';
import { useModal } from 'hooks/use-modal';
import React, { useMemo } from 'react';
import { ReactComponent as ArrowUpIcon } from 'assets/icons/long-arrow-up.svg';
import { ReactComponent as PremiumIcon } from 'assets/icons/premium.svg';
import VipImg from 'assets/icons/vip.png';
import { useTranslation } from 'react-i18next';
import { RadioGroup, RadioGroupItem } from 'components/common/radio-group';
import { Label } from 'components/common/label';
import ButtonPrimary from 'components/common/button/primary';
import {
    useGetApiV1ClientAdFeaturesQuery,
    usePostApiV1ClientPaymentsInitializeAdsUpgradePaymentMutation,
    usePostApiV1ClientPaymentsInitializePayWithBalancePaymentMutation,
} from 'service/api/allApis';
import { ADVERT_UPGRADE_PLAN } from 'types';
import { formatPrice } from 'lib/utils';
import { useSelector } from 'react-redux';
import { selectSettings } from 'store/features/settingsSlice';
import { Form, FormControl, FormField, FormItem } from 'components/common/form';
import { useForm } from 'react-hook-form';
import { selectUser } from 'store/features/userSlice';
import { toast } from 'sonner';
import { RenderIf } from 'components/common/render-if';

const AdvertUpgradeModal = () => {
    const { isOpen, type, onClose, data: modalData } = useModal();
    const { user } = useSelector(selectUser);
    const { currency } = useSelector(selectSettings);
    const { data } = useGetApiV1ClientAdFeaturesQuery({
        isPaginated: false,
    });
    const [mutatePaymentByCreditCard, { isLoading: payByCreditCardLoading }] =
        usePostApiV1ClientPaymentsInitializeAdsUpgradePaymentMutation();
    const [mutatePaymentByBalance, { isLoading: payByBalanceLoading }] =
        usePostApiV1ClientPaymentsInitializePayWithBalancePaymentMutation();
    const form = useForm({
        defaultValues: {
            planId: '',
            paymentMethod: 'credit-card',
        },
    });
    const isLoading = payByCreditCardLoading || payByBalanceLoading;
    const paymentMethod = form.watch('paymentMethod');

    const isModalOpen = isOpen && type == 'advertUpgrade';
    const { upgradeData } = modalData;
    const { t } = useTranslation();

    let header: React.ReactNode;
    const items = useMemo(() => {
        const items = [
            ...(data?.items?.find((item) => item.name === upgradeData?.type)?.pricings || []),
        ];
        return items.sort((a, b) => a.durationInDays! - b.durationInDays!);
    }, [data?.items, upgradeData]);

    const itemDurationLabel =
        upgradeData?.type === ADVERT_UPGRADE_PLAN.FORWARD ? t('general.time') : t('general.day');

    switch (upgradeData?.type) {
        case ADVERT_UPGRADE_PLAN.FORWARD:
            header = (
                <>
                    <div className="flex gap-x-2 items-center">
                        <ArrowUpIcon className="w-6 h-6" />
                        <h3 className="font-bold text-lg md:text-3xl">
                            {t('advert.move-forward')}
                        </h3>
                    </div>
                    <p className="w-full text-left">{t('advert.move-forward-description')}</p>
                </>
            );
            break;
        case ADVERT_UPGRADE_PLAN.VIP:
            header = (
                <>
                    <div className="flex gap-x-2 items-center">
                        <img
                            src={VipImg}
                            className="w-8 -translate-y-0.5 object-contain scale-125"
                        />
                        <h3 className="font-bold text-lg md:text-3xl">{t('advert.make-vip')}</h3>
                    </div>
                    <p className="w-full text-left">{t('advert.make-vip-description')}</p>
                </>
            );
            break;
        case ADVERT_UPGRADE_PLAN.PREMIUM:
            header = (
                <>
                    <div className="flex gap-x-2 items-center">
                        <PremiumIcon className="w-8 h-8" />
                        <h3 className="font-bold text-lg md:text-3xl">
                            {t('advert.make-premium')}
                        </h3>
                    </div>
                    <p className="w-full text-left">{t('advert.make-premium-description')}</p>
                </>
            );
            break;
    }

    async function onSubmit(values: { planId: string; paymentMethod: string }) {
        const planId = values.planId || items[0]?.id;
        if (!planId || !upgradeData?.advertId) return;

        if (values.paymentMethod === 'credit-card') {
            mutatePaymentByCreditCard({
                adsId: upgradeData.advertId,
                adFueaturePricingId: planId,
            })
                .unwrap()
                .then((res) => {
                    const { redirectUrl } = res as { redirectUrl: string };
                    if (redirectUrl) {
                        window.location.href = redirectUrl;
                    }
                    onClose();
                })
                .catch((e) => {
                    const errorMessage = e?.data?.Title ?? t('general.something-went-wrong');
                    toast.error(errorMessage);
                });
        } else if (values.paymentMethod === 'balance') {
            mutatePaymentByBalance({
                adsId: upgradeData.advertId,
                adFueaturePricingId: planId,
            })
                .unwrap()
                .then(() => {
                    toast.success(t('general.payment-success'));
                    onClose();
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                })
                .catch((e) => {
                    const errorMessage = e?.data?.Title ?? t('general.something-went-wrong');
                    toast.error(errorMessage);
                });
        }
    }

    return (
        <Modal
            isOpen={isModalOpen}
            onClose={onClose}
            modalContainerClassName="flex"
            modalClassName="w-full max-w-[550px] mx-6  bg-white rounded-2xl p-4 md:p-8"
        >
            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex flex-col justify-center items-center gap-y-7"
                >
                    {header}
                    <div className="w-full">
                        <h5 className="text-brend uppercase border-y border-brend w-full text-center py-1 md:py-2">
                            {t('advert.select-upgrade-plan')}
                        </h5>
                        <div className="my-4 md:my-6">
                            <FormField
                                control={form.control}
                                name="planId"
                                render={({ field }) => (
                                    <FormItem className="space-y-3">
                                        <FormControl>
                                            {!!items.length && (
                                                <RadioGroup
                                                    defaultValue={items[0].id}
                                                    onValueChange={field.onChange}
                                                >
                                                    {items.map((item) => (
                                                        <FormItem
                                                            key={item.id}
                                                            className="flex items-center space-x-6 md:text-lg pb-4 border-b border-brend-20 mb-3"
                                                        >
                                                            <FormControl>
                                                                <>
                                                                    <RadioGroupItem
                                                                        value={item.id!}
                                                                        id={item.id}
                                                                    />
                                                                    <Label htmlFor={item.id}>
                                                                        {item.durationInDays}{' '}
                                                                        {itemDurationLabel} /{' '}
                                                                        {formatPrice(
                                                                            item.price,
                                                                            +currency.exchangeRate,
                                                                        )}{' '}
                                                                        {currency.code}
                                                                    </Label>
                                                                </>
                                                            </FormControl>
                                                        </FormItem>
                                                    ))}
                                                </RadioGroup>
                                            )}
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <h5 className="text-brend uppercase border-y border-brend w-full text-center py-1 md:py-2">
                            {t('advert.select-payment-type')}
                        </h5>
                        <div className="mt-4 md:mt-6">
                            <RadioGroup
                                value={paymentMethod}
                                onValueChange={(value) => {
                                    form.setValue('paymentMethod', value);
                                }}
                            >
                                <div className="flex items-center space-x-6 md:text-lg pb-4 border-b border-brend-20 mb-3">
                                    <RadioGroupItem value="credit-card" id="credit-card" />
                                    <Label htmlFor="credit-card">
                                        {t('cabinet.payment.with-card')}
                                    </Label>
                                </div>
                                <RenderIf condition={!!user}>
                                    <div className="flex items-center space-x-6 md:text-lg pb-4 border-b border-brend-20 mb-3">
                                        <RadioGroupItem value="balance" id="balance" />
                                        <Label htmlFor="balance">
                                            {t('cabinet.payment.with-balance')} (
                                            {formatPrice(user?.balance)} AZN)
                                        </Label>
                                    </div>
                                </RenderIf>
                            </RadioGroup>
                        </div>
                    </div>
                    <ButtonPrimary
                        loading={isLoading}
                        type="submit"
                        className="w-full"
                        text={t('general.pay')}
                    />
                </form>
            </Form>
        </Modal>
    );
};

export default AdvertUpgradeModal;
